// The admin console's Integrations tab (.design-specs/admin-console.plan.md §5): every sync job by provider and
// health. "Stale" is the scheduler's own definition, served with the panel. Below it, the per-band debugger.
import { Card, DataTable, EmptyState, Tag, formatRelativeTime, type DataTableColumn } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import {
  useAdminIntegrations,
  type AdminAttentionRow,
  type AdminProviderHealth,
} from "../../app/hooks/useAdmin.js";
import { BandDebugger } from "./BandDebugger.js";
import styles from "./AdminDashboard.module.scss";

const count = (n: number | undefined) => (n ? n.toLocaleString() : <span className={styles.zero}>0</span>);

/** A count that only matters when it is not zero: tinted, so a healthy table reads as a column of quiet zeros. */
function IssueCount({ n, tone }: { n: number | undefined; tone: "danger" | "warning" | "neutral" }) {
  if (!n) return <span className={styles.zero}>0</span>;
  return (
    <Tag tone={tone} size="sm">
      {n.toLocaleString()}
    </Tag>
  );
}

const PROVIDER_COLUMNS: DataTableColumn<AdminProviderHealth>[] = [
  { key: "provider", header: "Provider", cell: (p) => <strong>{p.provider}</strong> },
  { key: "bands", header: "Bands", align: "end", cell: (p) => count(p.bands) },
  { key: "jobs", header: "Jobs", align: "end", cell: (p) => count(p.jobs) },
  { key: "healthy", header: "Healthy", align: "end", cell: (p) => count(p.healthy) },
  { key: "blocked", header: "Blocked", align: "end", cell: (p) => <IssueCount n={p.blocked} tone="danger" /> },
  { key: "stale", header: "Stale", align: "end", cell: (p) => <IssueCount n={p.stale} tone="danger" /> },
  { key: "failing", header: "Failing", align: "end", cell: (p) => <IssueCount n={p.failing} tone="warning" /> },
  { key: "never", header: "Never landed", align: "end", cell: (p) => <IssueCount n={p.neverLanded} tone="neutral" /> },
  { key: "budget", header: "Out of budget", align: "end", cell: (p) => <IssueCount n={p.budgetExhausted} tone="warning" /> },
  { key: "paused", header: "Paused", align: "end", cell: (p) => <IssueCount n={p.paused} tone="neutral" /> },
];

const ISSUE_TONE: Record<string, "danger" | "warning"> = {
  blocked: "danger",
  "connect-failed": "danger",
  "no-tracks": "warning",
  stale: "danger",
  failing: "warning",
};

const attentionColumns = (onBand: (bandId: string) => void): DataTableColumn<AdminAttentionRow>[] => [
  {
    key: "band",
    header: "Band",
    cell: (a) => (
      <button type="button" className={styles.linkButton} onClick={() => a.bandId && onBand(a.bandId)}>
        {a.bandName}
      </button>
    ),
  },
  { key: "job", header: "Job", cell: (a) => <code className={styles.code}>{`${a.provider} · ${a.kind}`}</code> },
  {
    key: "issue",
    header: "Issue",
    cell: (a) => (
      <Tag tone={ISSUE_TONE[a.issue ?? ""] ?? "neutral"} size="sm">
        {a.issue}
      </Tag>
    ),
  },
  { key: "detail", header: "Detail", cell: (a) => a.detail ?? "—" },
  { key: "since", header: "Since", cell: (a) => formatRelativeTime(a.since) ?? "—" },
  { key: "data", header: "Last data", cell: (a) => formatRelativeTime(a.lastLandOn) ?? "never" },
];

export interface IntegrationsTabProps {
  /** The band the debugger shows (`?band=`), or none. */
  bandId?: string;
  onBand: (bandId: string | undefined) => void;
}

/** Every sync job on the platform by provider and health, plus the jobs that need a person. */
export function IntegrationsTab({ bandId, onBand }: IntegrationsTabProps) {
  return (
    <>
      <PlatformIntegrations onBand={onBand} />
      <BandDebugger bandId={bandId} onBand={onBand} />
    </>
  );
}

function PlatformIntegrations({ onBand }: { onBand: (bandId: string | undefined) => void }) {
  const q = useAdminIntegrations();

  if (q.isLoading) return <Card title="Integrations" subtitle="Reading every sync job…" />;
  if (q.isError) {
    return (
      <Card title="Integrations">
        <p className={styles.error}>{errorMessage(q.error, "Could not read integrations health.")}</p>
      </Card>
    );
  }
  const panel = q.data;
  if (!panel) return null;

  const jobs = panel.providers?.reduce((n, p) => n + (p.jobs ?? 0), 0) ?? 0;
  const healthy = panel.providers?.reduce((n, p) => n + (p.healthy ?? 0), 0) ?? 0;

  return (
    <Card
      title="Integrations"
      subtitle={`Every sync job on the platform. Stale = no new data for ${panel.staleAfterHours}h. Read ${formatRelativeTime(panel.generatedOnUtc) ?? "just now"}.`}
    >
      <div className={styles.stack}>
        <dl className={styles.headline}>
          <div>
            <dt>Jobs</dt>
            <dd>{jobs.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Healthy</dt>
            <dd>{healthy.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Need attention</dt>
            <dd data-alert={(panel.attentionTotal ?? 0) > 0 || undefined}>{(panel.attentionTotal ?? 0).toLocaleString()}</dd>
          </div>
        </dl>

        {(panel.providers?.length ?? 0) === 0 ? (
          <EmptyState title="No sync jobs" description="No band has connected anything yet." />
        ) : (
          <DataTable columns={PROVIDER_COLUMNS} rows={panel.providers ?? []} rowKey={(p) => p.provider ?? ""} />
        )}

        {(panel.attention?.length ?? 0) > 0 && (
          <>
            <h3 className={styles.subhead}>
              Needs attention
              {(panel.attentionTotal ?? 0) > (panel.attention?.length ?? 0) &&
                ` · worst ${panel.attention?.length} of ${panel.attentionTotal}`}
            </h3>
            <DataTable
              columns={attentionColumns(onBand)}
              rows={panel.attention ?? []}
              rowKey={(a) => `${a.bandId}/${a.provider}/${a.kind}`}
              maxBlockSize="28rem"
            />
          </>
        )}
      </div>
    </Card>
  );
}
